import { create } from 'zustand';
import { authApi } from '../services/api';
import { toast } from 'react-toastify';

const useAuthStore = create((set, get) => ({  // Estado
  token: localStorage.getItem('token') || null,
  user: JSON.parse(localStorage.getItem('user') || 'null'),
  isAuthenticated: false, // Se establece después de verificar con el servidor
  loading: false, // Solo para acciones manuales del usuario (login)
  verifying: false, // Para verificaciones automáticas (menos intrusivo)
  error: null,
  message: null,
  initialCheckDone: false, // Para saber si ya se hizo la verificación inicial

  // Acciones
  login: async (credentials) => {
    set({ loading: true, error: null, message: null });
    try {
      const response = await authApi.login(credentials);
      const { status, message: apiMessage, token, user } = response.data;
      
  
      
      if (!status) {
        // Si el status es false, mostramos el mensaje como error
        set({ error: apiMessage, loading: false });
        toast.error(apiMessage);
        return { status, message: apiMessage };
      }
      
      // Si el login es correcto, guardamos los datos
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
        set({ 
        token, 
        user, 
        isAuthenticated: true, 
        loading: false,
        initialCheckDone: true,
        message: apiMessage
      });
      
      toast.success(apiMessage);
      return { status, message: apiMessage };
    } catch (error) {
      // Este bloque se ejecutará solo si hay un error HTTP (no 2xx)
      console.error('Error en login:', error);
      
      const errorMessage = error.response?.data?.message || 
                          'Error al conectar con el servidor';
      
      set({ 
        error: errorMessage, 
        loading: false 
      });
      
      toast.error(errorMessage);
      return { status: false, message: errorMessage };
    }
  },

  logout: async () => {
    set({ loading: true, error: null });
    try {
      if (get().token) {
        await authApi.logout();
      }
      
      // Limpiar localStorage y estado
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      set({ token: null, user: null, isAuthenticated: false, loading: false });
      toast.success('Sesión cerrada exitosamente');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // Aún así limpiamos el estado local si hay error en el servidor
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      set({ token: null, user: null, isAuthenticated: false, loading: false });
      toast.error('Error al cerrar sesión');
    }
  },  checkAuth: async () => {
    const token = get().token;
    
    if (!token) {
      console.log('checkAuth: No hay token, estableciendo como no autenticado');
      set({ 
        isAuthenticated: false, 
        user: null, 
        loading: false, 
        initialCheckDone: true,
        error: null 
      });
      return false;
    }
    
    // No mostrar loading para evitar molestias al usuario
    try {

      const response = await authApi.perfil();     
      
      // Verificar primero si la respuesta es un objeto con la propiedad data
      const userData = response.data?.user || response.data;

      
      // Verificar que el usuario tenga la información necesaria
      if (!userData || !userData.rol_id) {
        console.warn('Usuario sin rol válido:', userData);
        localStorage.removeItem('token');
        localStorage.removeItem('user');set({ 
          token: null, 
          user: null, 
          isAuthenticated: false, 
          loading: false,
          initialCheckDone: true,
          error: 'Usuario sin rol asignado'
        });
        return false;
      }
        // Actualizar localStorage con datos frescos
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', get().token); // Reasegurar que el token está guardado
      
      // Asegurarnos que el estado se actualiza correctamente
      set({ 
        user: userData, 
        token: get().token,
        isAuthenticated: true, 
        loading: false,
        initialCheckDone: true,
        error: null
      });
      
  
      return true;    } catch (error) {
      console.error('checkAuth: Error al verificar token:', error);
      
      // Si hay error, limpiar la sesión completamente
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      set({ 
        token: null, 
        user: null, 
        isAuthenticated: false, 
        loading: false,
        initialCheckDone: true,
        error: 'Sesión expirada o inválida'
      });
      
      return false;
    }
  },
  // Método para obtener roles
  getRoles: async () => {
    try {
      const response = await authApi.roles();
      return response.data;
    } catch (error) {
      console.error('Error al obtener roles:', error);
      throw error;
    }
  },

  // Métodos de verificación de permisos
  hasRole: (roleName) => {
    const user = get().user;
    return user?.rol?.nombre === roleName;
  },

  hasRoleId: (roleId) => {
    const user = get().user;
    return user?.rol_id === roleId;
  },

  isAdmin: () => {
    return get().hasRoleId(1) || get().hasRole('Administrador');
  },

  isAdministrativo: () => {
    return get().hasRoleId(2) || get().hasRole('Administrativo');
  },

  isAuditor: () => {
    return get().hasRoleId(3) || get().hasRole('Auditor');
  },

  canManageData: () => {
    return get().isAdmin() || get().isAdministrativo();
  },

  getUserRole: () => {
    const user = get().user;
    return {
      id: user?.rol_id,
      name: user?.rol?.nombre,
      permissions: user?.rol?.permisos || []
    };
  },

  // Actualizar perfil de usuario
  updateUserProfile: (userData) => {
    const currentUser = get().user;
    const updatedUser = { ...currentUser, ...userData };
    
    localStorage.setItem('user', JSON.stringify(updatedUser));
    set({ user: updatedUser });
  },
  // Limpiar mensajes de éxito y error
  clearMessages: () => set({ error: null, message: null }),
  // Función para inicializar la autenticación al cargar la app
  initializeAuth: async () => {
    const token = get().token;
    const user = get().user;
    
 
    
    if (!token) {
      // Si no hay token, no está autenticado
      console.log('initializeAuth: No hay token, marcando como no autenticado');
      set({ 
        isAuthenticated: false, 
        loading: false, 
        initialCheckDone: true 
      });
      return false;
    }
    
    try {
      if (user && user.rol_id) {
 
        // No establecemos isAuthenticated=true hasta verificar con el servidor
        const result = await get().checkAuth();
  
        return result;
      } else {
        // Token existe pero no hay usuario válido
    
        const result = await get().checkAuth();
  
        return result;
      }
    } catch (error) {
      console.error('initializeAuth: Error al verificar autenticación', error);
      // En caso de error, aseguramos que el usuario no queda autenticado
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      set({ 
        token: null,
        user: null,
        isAuthenticated: false, 
        loading: false, 
        initialCheckDone: true,
        error: 'Error al verificar la sesión'
      });
      return false;
    }
  },

  // Función para limpiar completamente la sesión (útil para debugging)
  clearSession: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    set({ 
      token: null, 
      user: null, 
      isAuthenticated: false, 
      loading: false,
      error: null,
      message: null 
    });

  }
}));

export default useAuthStore;